import { Button } from "@nimit9/signet-ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@nimit9/signet-ui/components/dialog"
import { Label } from "@nimit9/signet-ui/components/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nimit9/signet-ui/components/select"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useRef, useState } from "react"
import { useAccounts } from "@/hooks/use-accounts"
import { useOwnerLabels } from "@/hooks/use-owner-labels"
import { api } from "@/lib/api"
import { useSetSharedFile, useSharedFile } from "@/store/selectors"
import { AutoCategorizeDialog } from "./-auto-categorize-dialog"
import { overlapWarningFrom } from "./-overlap-warning"

type ColMapping = {
  dateCol: string
  descriptionCol: string
  debitCol: string
  creditCol: string
  amountCol: string
}

export function CsvImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [step, setStep] = useState<"upload" | "map" | "preview" | "done">("upload")
  // After an import, hand straight over to auto-categorise (UAT J4-01/J4-05).
  const [autoCatOpen, setAutoCatOpen] = useState(false)
  const [csvText, setCsvText] = useState("")
  const [headers, setHeaders] = useState<string[]>([])
  const [preview, setPreview] = useState<Record<string, string>[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [mapping, setMapping] = useState<ColMapping>({
    dateCol: "",
    descriptionCol: "",
    debitCol: "",
    creditCol: "",
    amountCol: "",
  })
  const [owner, setOwner] = useState<"user_1" | "user_2" | "joint">("joint")
  // Default the owner to the person importing (UAT J4-03); they can still pick Joint.
  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => api.get<{ success: boolean; data: { owner: string | null } }>("/profile"),
    enabled: open,
  })
  const myOwner = profile?.data.owner
  useEffect(() => {
    if (myOwner === "user_1" || myOwner === "user_2") setOwner(myOwner)
  }, [myOwner])
  // Which account the file belongs to: scopes the overlap check and tags the rows (UAT J4-03).
  const [accountId, setAccountId] = useState("")
  const { data: accountsResp } = useAccounts({ enabled: open })
  const accounts = accountsResp?.data ?? []
  const ownerLabels = useOwnerLabels()
  const ownerItems = {
    user_1: ownerLabels.user_1 ?? "Person 1",
    user_2: ownerLabels.user_2 ?? "Person 2",
    joint: ownerLabels.joint ?? "Joint",
  }
  const [skipDuplicates, setSkipDuplicates] = useState(true)
  // The dry-run import check shown before anything is written — three
  // numbers, one confirm (mirrors the MCP import tool's two-phase dry-run,
  // statement-import.ts). This is exact-key dedupe (date+description+amount
  // +type, lib/dedup.ts) against existing rows, NOT the fuzzy
  // packages/reconciliation matcher — "import check" rather than
  // "reconciliation" so the copy doesn't overclaim what it does (M3).
  const [importCheck, setImportCheck] = useState<{
    imported: number
    skipped: number
    duplicatesSkipped: number
  } | null>(null)
  const [result, setResult] = useState<{
    imported: number
    skipped: number
    duplicatesSkipped: number
  } | null>(null)
  const [parseError, setParseError] = useState("")
  // A >50% date-range overlap 409s instead of previewing (transactions.ts) —
  // surface the real message and let the person re-run with force:true
  // instead of a dead-end raw "HTTP 409" (M4).
  const [overlapWarning, setOverlapWarning] = useState<{
    message: string
    overlapPct: number
  } | null>(null)
  const [force, setForce] = useState(false)
  const [isParsing, setIsParsing] = useState(false)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  // Synchronous guard: two fast clicks land before the disabled state re-renders,
  // and each would POST the same rows (the server's dedupe read can race).
  const importingRef = useRef(false)

  const sharedFile = useSharedFile()
  const setSharedFile = useSetSharedFile()

  function reset() {
    setStep("upload")
    setCsvText("")
    setHeaders([])
    setPreview([])
    setTotalRows(0)
    setMapping({ dateCol: "", descriptionCol: "", debitCol: "", creditCol: "", amountCol: "" })
    setImportCheck(null)
    setResult(null)
    setParseError("")
    setOverlapWarning(null)
    setForce(false)
  }

  // Stable identity (only state setters + the api module inside), so the
  // shared-file effect below doesn't re-run on every render.
  const processFile = useCallback(async (file: File) => {
    setParseError("")
    setIsParsing(true)
    try {
      const text = await file.text()
      setCsvText(text)
      const form = new FormData()
      form.append("file", file)
      const res = await api.postForm<{
        success: boolean
        error?: string
        headers: string[]
        preview: Record<string, string>[]
        totalRows: number
        detected: ColMapping
      }>("/transactions/import/parse", form)
      if (!res.success) throw new Error(res.error ?? "Parse failed")
      setHeaders(res.headers)
      setPreview(res.preview)
      setTotalRows(res.totalRows)
      setMapping({
        dateCol: res.detected.dateCol ?? "",
        descriptionCol: res.detected.descriptionCol ?? "",
        debitCol: res.detected.debitCol ?? "",
        creditCol: res.detected.creditCol ?? "",
        amountCol: res.detected.amountCol ?? "",
      })
      setStep("map")
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "Failed to parse file")
    } finally {
      setIsParsing(false)
    }
  }, [])

  async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    await processFile(file)
  }

  // Hand-off from the PWA share_target landing page (/share-target): a CSV
  // shared from the OS share sheet lands here pre-selected instead of making
  // the person pick the same file again.
  useEffect(() => {
    if (open && sharedFile) {
      const file = sharedFile
      setSharedFile(null)
      processFile(file)
    }
  }, [open, sharedFile, setSharedFile, processFile])

  async function runPreview(forceOverride?: boolean) {
    const useForce = forceOverride ?? force
    setIsPreviewing(true)
    setParseError("")
    setOverlapWarning(null)
    try {
      const res = await api.post<{
        success: boolean
        error?: string
        imported: number
        skipped: number
        duplicatesSkipped: number
      }>("/transactions/import/confirm", {
        csvText,
        mapping,
        owner,
        accountId: accountId || null,
        skipDuplicates,
        dryRun: true,
        force: useForce,
      })
      if (!res.success) throw new Error(res.error ?? "Couldn't check this import")
      setImportCheck(res)
      setStep("preview")
    } catch (err) {
      const overlap = overlapWarningFrom(err)
      if (overlap) {
        setOverlapWarning({ message: overlap.message, overlapPct: overlap.overlapPct })
      } else {
        setParseError(err instanceof Error ? err.message : "Couldn't check this import")
      }
    } finally {
      setIsPreviewing(false)
    }
  }

  async function confirm() {
    if (importingRef.current) return
    importingRef.current = true
    setIsImporting(true)
    try {
      const res = await api.post<{
        success: boolean
        error?: string
        imported: number
        skipped: number
        duplicatesSkipped: number
      }>("/transactions/import/confirm", {
        csvText,
        mapping,
        owner,
        accountId: accountId || null,
        skipDuplicates,
        dryRun: false,
        force,
      })
      if (!res.success) throw new Error(res.error ?? "Import failed")
      setResult(res)
      setStep("done")
      qc.invalidateQueries({ queryKey: ["transactions"] })
    } catch (err) {
      // force already carries through from the preview step's "Import
      // anyway", so this should be rare — but stay defensive against a
      // fresh overlap (e.g. another import landed between preview and
      // confirm) instead of surfacing a raw "HTTP 409" (M4).
      const overlap = overlapWarningFrom(err)
      if (overlap) {
        setParseError(overlap.message)
      } else {
        setParseError(err instanceof Error ? err.message : "Import failed")
      }
    } finally {
      importingRef.current = false
      setIsImporting(false)
    }
  }

  const colItems = { "": "— skip —", ...Object.fromEntries(headers.map((h) => [h, h])) }
  const colSelect = (label: string, key: keyof ColMapping, required = false) => (
    <div>
      <Label className="text-label tracking-wider text-text-muted">
        {label}
        {required && <span className="ml-1 text-text-muted">*</span>}
      </Label>
      <Select
        value={mapping[key]}
        onValueChange={(v) => setMapping((m) => ({ ...m, [key]: v }))}
        items={colItems}
      >
        <SelectTrigger className="mt-1">
          <SelectValue placeholder="— skip —" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="">— skip —</SelectItem>
          {headers.map((h) => (
            <SelectItem key={h} value={h}>
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            reset()
            onClose()
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import from CSV</DialogTitle>
          </DialogHeader>

          {step === "upload" && (
            <div className="min-w-0 space-y-4 pt-2">
              <p className="text-body text-text-secondary">
                Upload a bank statement CSV. Supports HDFC, ICICI, SBI, and most standard formats.
              </p>
              <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border-default p-10 text-center hover:border-accent-green/50">
                <span className="text-body text-text-muted">
                  {isParsing ? "Parsing…" : "Click to select a CSV file"}
                </span>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={onFileChange}
                  disabled={isParsing}
                />
              </label>
              {parseError && <p className="text-caption text-accent-red">{parseError}</p>}
            </div>
          )}

          {step === "map" && (
            <div className="min-w-0 space-y-4 pt-2">
              <p className="text-body text-text-secondary">
                {totalRows} rows found. Map columns below — auto-detected where possible.
              </p>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {colSelect("Date column", "dateCol", true)}
                {colSelect("Description column", "descriptionCol", true)}
                {colSelect("Debit column", "debitCol")}
                {colSelect("Credit column", "creditCol")}
                {colSelect("Amount column (signed)", "amountCol")}
                <div>
                  <Label className="text-label tracking-wider text-text-muted">Account</Label>
                  <Select
                    value={accountId}
                    onValueChange={(v) => setAccountId((v as string) ?? "")}
                    items={{
                      "": "Not set",
                      ...Object.fromEntries(accounts.map((a) => [a.id, a.name])),
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Which account is this?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Not set</SelectItem>
                      {accounts.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-label tracking-wider text-text-muted">Owner</Label>
                  <Select
                    value={owner}
                    onValueChange={(v) => setOwner(v as typeof owner)}
                    items={ownerItems}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Owner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user_1">{ownerItems.user_1}</SelectItem>
                      <SelectItem value="user_2">{ownerItems.user_2}</SelectItem>
                      <SelectItem value="joint">{ownerItems.joint}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <label className="flex cursor-pointer items-center gap-2 text-body text-text-secondary">
                <input
                  type="checkbox"
                  checked={skipDuplicates}
                  onChange={(e) => setSkipDuplicates(e.target.checked)}
                  className="rounded"
                />
                Skip transactions already in Paisa
              </label>

              {/* Preview table */}
              {preview.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-border-subtle">
                  <table className="w-full text-caption">
                    <thead className="bg-bg-elevated">
                      <tr>
                        {headers.map((h) => (
                          <th key={h} className="px-3 py-2 text-left font-medium text-text-muted">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((row, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: preview rows have no stable key
                        <tr key={i} className="border-t border-border-subtle">
                          {headers.map((h) => (
                            <td key={h} className="px-3 py-1.5 text-text-secondary">
                              {row[h]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {parseError && <p className="text-caption text-accent-red">{parseError}</p>}

              {overlapWarning && (
                <div className="space-y-2 rounded-lg border border-accent-amber/40 bg-accent-amber/5 p-3">
                  <p className="text-caption text-accent-amber">{overlapWarning.message}</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setForce(true)
                      runPreview(true)
                    }}
                    disabled={isPreviewing}
                  >
                    {isPreviewing ? "Checking…" : "Import anyway"}
                  </Button>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={() => runPreview()}
                  disabled={isPreviewing || !mapping.dateCol || !mapping.descriptionCol}
                >
                  {isPreviewing ? "Checking…" : `Preview ${totalRows} rows`}
                </Button>
                <Button variant="ghost" onClick={reset}>
                  Back
                </Button>
              </div>
            </div>
          )}

          {step === "preview" && importCheck && (
            <div className="min-w-0 space-y-4 pt-2">
              <p className="text-label uppercase tracking-wider text-text-muted">Import check</p>
              <p className="text-body text-text-secondary">
                Here's what'll happen — nothing has been written yet.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[
                  { label: "New", value: importCheck.imported, color: "text-accent-green" },
                  {
                    label: "Already in Paisa",
                    value: importCheck.duplicatesSkipped,
                    color: "text-text-muted",
                  },
                  {
                    label: "Need a look",
                    value: importCheck.skipped,
                    color: "text-accent-amber",
                  },
                ].map((c) => (
                  <div
                    key={c.label}
                    className="rounded-lg border border-border-subtle p-3 text-center"
                  >
                    <p className={`text-stat font-medium ${c.color}`}>{c.value}</p>
                    <p className="text-label text-text-muted">{c.label}</p>
                  </div>
                ))}
              </div>
              {importCheck.skipped > 0 && (
                <p className="text-caption text-text-muted">
                  "Need a look" rows couldn't be read with this mapping — a missing date, empty
                  description, or zero amount. Go back and check the column mapping if this seems
                  high.
                </p>
              )}
              {parseError && <p className="text-caption text-accent-red">{parseError}</p>}
              <div className="flex gap-2">
                <Button onClick={confirm} disabled={isImporting || importCheck.imported === 0}>
                  {isImporting ? "Importing…" : `Import ${importCheck.imported} rows`}
                </Button>
                <Button variant="ghost" onClick={() => setStep("map")}>
                  Back
                </Button>
              </div>
            </div>
          )}

          {step === "done" && result && (
            <div className="min-w-0 space-y-4 pt-2">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[
                  { label: "Imported", value: result.imported, color: "text-accent-green" },
                  {
                    label: "Invalid rows skipped",
                    value: result.skipped,
                    color: "text-text-muted",
                  },
                  {
                    label: "Duplicates skipped",
                    value: result.duplicatesSkipped,
                    color: "text-text-muted",
                  },
                ].map((c) => (
                  <div
                    key={c.label}
                    className="rounded-lg border border-border-subtle p-3 text-center"
                  >
                    <p className={`text-title font-medium ${c.color}`}>{c.value}</p>
                    <p className="text-caption text-text-muted">{c.label}</p>
                  </div>
                ))}
              </div>
              {result.imported > 0 && (
                <Button
                  className="w-full"
                  onClick={() => {
                    reset()
                    onClose()
                    setAutoCatOpen(true)
                  }}
                >
                  Categorise them now
                </Button>
              )}
              <Button
                className="w-full"
                variant={result.imported > 0 ? "ghost" : "default"}
                onClick={() => {
                  reset()
                  onClose()
                }}
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AutoCategorizeDialog open={autoCatOpen} onClose={() => setAutoCatOpen(false)} />
    </>
  )
}
